import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { TopBar } from "@/components/layout/TopBar";
import { ScoreRing, AIThinkingState } from "@/components/AltioraComponents";
import PrioritiesWidget from "@/components/PrioritiesWidget";
import CopilotWidget from "@/components/CopilotWidget";
import TrajectoryLens from "@/components/TrajectoryLens";
import {
  ArrowUpRight, Bell, BadgeCheck, TrendingUp, CalendarClock,
  FileBarChart2, Sparkles, ShieldCheck, LayoutGrid, Info, AlertTriangle, CheckCircle2,
} from "lucide-react";

const SEV_ICON = {
  info: Info, success: CheckCircle2, warning: AlertTriangle, danger: AlertTriangle,
};
const SEV_COLOR = {
  info: "#8FCAEF", success: "#059669", warning: "#F2C98A", danger: "#E88A93",
};

export default function Dashboard() {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/dashboard/personal");
        setData(r.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-full" data-testid="dashboard-personal-loading">
        <TopBar title="ALTIORA" subtitle={lang === "fr" ? "Chargement de votre trajectoire…" : "Loading your trajectory…"} />
        <div className="px-6 md:px-10 py-10 max-w-7xl mx-auto space-y-6">
          <AIThinkingState lang={lang} />
        </div>
      </div>
    );
  }

  const { score, priorities, strengths, gaps, recent_reports, alerts, unread_notifications, subscription } = data;
  const displayName = data.user?.name || (data.user?.email || "").split("@")[0] || "";

  return (
    <div className="min-h-full" data-testid="dashboard-personal">
      <TopBar
        title={lang === "fr" ? `Bonjour, ${displayName}` : `Hello, ${displayName}`}
        subtitle={lang === "fr" ? "Voici l'état actuel de votre trajectoire ALTIORA." : "Here's the current state of your ALTIORA trajectory."}
      />

      <div className="px-6 md:px-10 py-8 md:py-10 max-w-7xl mx-auto space-y-8">
        {/* Trajectory Lens — M1 comprehension + M2 next action (doctrine v2 core) */}
        <TrajectoryLens displayName={displayName} />

        {/* Copilot — smart human recommendation */}
        <CopilotWidget lang={lang} />

        {/* Priorities — orchestration engine widget */}
        <PrioritiesWidget lang={lang} />

        {/* Subscription banner */}
        {subscription && !subscription.connected && (
          <div className="rounded-lg border border-[#0B2545]/25 bg-gradient-to-r from-[#0B2545]/5 via-transparent to-transparent p-5 flex items-center justify-between altiora-transition">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                {lang === "fr" ? "Essai gratuit" : "Free trial"}
              </div>
              <div className="mt-1 font-display text-lg text-[#1F2937] tracking-tight">
                {lang === "fr" ? "Activez votre plan pour débloquer toute la puissance" : "Activate your plan to unlock full power"}
              </div>
              <div className="mt-1 text-xs text-[#5B5B62]">
                {lang === "fr" ? "7 jours offerts. Sans engagement." : "7 days on us. No commitment."}
              </div>
            </div>
            <Link
              to="/app/finance"
              data-testid="dashboard-cta-finance"
              className="inline-flex items-center gap-2 bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition"
            >
              {lang === "fr" ? "Voir les plans" : "View plans"}
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
        )}

        {/* Hero row : Score + Dimensions */}
        <div className="grid grid-cols-12 gap-4">
          <div data-testid="dashboard-score-card" className="col-span-12 md:col-span-5 rounded-lg border border-[#E4E1D8] bg-white p-6 md:p-8 flex flex-col items-center justify-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545] mb-4">
              {lang === "fr" ? "Score ALTIORA" : "ALTIORA Score"}
            </div>
            <ScoreRing
              value={score?.score || 0}
              label={score?.band || "—"}
              hint={lang === "fr" ? "sur 100" : "out of 100"}
            />
            <div className="mt-6 flex items-center gap-1.5 text-xs text-[#5B5B62]">
              <ShieldCheck className="h-3.5 w-3.5 text-[#059669]" strokeWidth={1.8} />
              <span>{lang === "fr" ? "Certifié Trusted Intelligence™" : "Trusted Intelligence™ certified"}</span>
            </div>
          </div>

          <div data-testid="dashboard-dimensions-card" className="col-span-12 md:col-span-7 rounded-lg border border-[#E4E1D8] bg-white p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Dimensions de vie" : "Life dimensions"}
                </div>
                <div className="mt-1 font-display text-lg text-[#1F2937] tracking-tight">
                  {lang === "fr" ? "Vos 6 dimensions" : "Your 6 dimensions"}
                </div>
              </div>
              <TrendingUp className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
            </div>
            <div className="space-y-4">
              {(score?.dimensions || []).map((d) => (
                <div key={d.key} data-testid={`dashboard-dim-${d.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-[#1F2937]">{d[`label_${lang}`] || d.label || d.key}</span>
                    <span className="font-mono text-xs text-[#5B5B62]">{d.value}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#EBE8DE] overflow-hidden">
                    <div
                      className="h-full altiora-transition"
                      style={{
                        width: `${Math.max(0, Math.min(100, d.value))}%`,
                        background: "linear-gradient(90deg, #0B2545, #123163)",
                      }}
                    />
                  </div>
                </div>
              ))}
              {(!score?.dimensions || score.dimensions.length === 0) && (
                <div className="text-xs text-[#8B8B8F] font-mono">
                  {lang === "fr" ? "Complétez votre diagnostic pour révéler vos dimensions." : "Complete your diagnostic to reveal your dimensions."}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions 30 / 90 / 365 */}
        <div data-testid="dashboard-actions" className="rounded-lg border border-[#E4E1D8] bg-white p-6 md:p-8">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                {lang === "fr" ? "Plan d'action" : "Action plan"}
              </div>
              <div className="mt-1 font-display text-lg text-[#1F2937] tracking-tight">
                {lang === "fr" ? "Prochaines actions — 30 / 90 / 365 jours" : "Next actions — 30 / 90 / 365 days"}
              </div>
            </div>
            <CalendarClock className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: "days_30", label_fr: "30 jours", label_en: "30 days", tint: "#0B2545" },
              { key: "days_90", label_fr: "90 jours", label_en: "90 days", tint: "#123163" },
              { key: "days_365", label_fr: "365 jours", label_en: "365 days", tint: "#8B8B8F" },
            ].map((col) => (
              <div key={col.key} className="rounded-md border border-[#E4E1D8]/60 bg-[#F8F7F3]/50 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: col.tint }}>
                  {col[`label_${lang}`]}
                </div>
                <ul className="space-y-2.5">
                  {(priorities?.[col.key] || []).slice(0, 5).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1F2937] leading-snug">
                      <span className="mt-1 h-1 w-1 rounded-full flex-shrink-0" style={{ background: col.tint }} />
                      <span>{item}</span>
                    </li>
                  ))}
                  {(priorities?.[col.key] || []).length === 0 && (
                    <li className="text-xs text-[#8B8B8F] font-mono">—</li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts + Reports */}
        <div className="grid grid-cols-12 gap-4">
          <div data-testid="dashboard-alerts" className="col-span-12 md:col-span-6 rounded-lg border border-[#E4E1D8] bg-white p-6">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Alertes" : "Alerts"}
                </div>
                <div className="mt-1 font-display text-base text-[#1F2937] tracking-tight">
                  {unread_notifications > 0
                    ? (lang === "fr" ? `${unread_notifications} non lues` : `${unread_notifications} unread`)
                    : (lang === "fr" ? "Tout est à jour" : "All caught up")}
                </div>
              </div>
              <Bell className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
            </div>
            <ul className="space-y-2">
              {(alerts || []).slice(0, 4).map((a) => {
                const Ico = SEV_ICON[a.severity] || Info;
                return (
                  <li key={a.notification_id} className="flex items-start gap-3 rounded-md border border-[#E4E1D8]/70 bg-[#F8F7F3]/40 px-3 py-2.5">
                    <Ico className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.8} style={{ color: SEV_COLOR[a.severity] || "#5B5B62" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#1F2937] truncate">{a.title}</div>
                      {a.body && <div className="text-xs text-[#5B5B62] mt-0.5 line-clamp-2">{a.body}</div>}
                    </div>
                  </li>
                );
              })}
              {(!alerts || alerts.length === 0) && (
                <li className="text-xs text-[#8B8B8F] font-mono">{lang === "fr" ? "Aucune alerte." : "No alerts."}</li>
              )}
            </ul>
          </div>

          <div data-testid="dashboard-recent-reports" className="col-span-12 md:col-span-6 rounded-lg border border-[#E4E1D8] bg-white p-6">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "Historique" : "History"}
                </div>
                <div className="mt-1 font-display text-base text-[#1F2937] tracking-tight">
                  {lang === "fr" ? "Rapports récents" : "Recent reports"}
                </div>
              </div>
              <FileBarChart2 className="h-4 w-4 text-[#0B2545]" strokeWidth={1.8} />
            </div>
            <ul className="space-y-1">
              {(recent_reports || []).map((r) => (
                <li key={r.report_id}>
                  <Link to="/app/reports" className="group flex items-center justify-between rounded-md px-3 py-2 hover:bg-[#EBE8DE] altiora-transition">
                    <div className="min-w-0">
                      <div className="text-sm text-[#1F2937] truncate">{r.title}</div>
                      <div className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">
                        {r.report_type}
                      </div>
                    </div>
                    <div className="font-mono text-[10px] text-[#5B5B62]">
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </Link>
                </li>
              ))}
              {(!recent_reports || recent_reports.length === 0) && (
                <li className="text-xs text-[#8B8B8F] font-mono px-3 py-2">
                  {lang === "fr" ? "Aucun rapport encore." : "No reports yet."}
                </li>
              )}
            </ul>
            <Link to="/app/reports" className="mt-4 inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#123163] font-mono uppercase tracking-widest">
              {lang === "fr" ? "Tout voir" : "See all"}
              <ArrowUpRight className="h-3 w-3" strokeWidth={1.8} />
            </Link>
          </div>
        </div>

        {/* Strengths / Gaps */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6 rounded-lg border border-[#E4E1D8] bg-white p-6">
            <div className="flex items-center gap-2 mb-3">
              <BadgeCheck className="h-4 w-4 text-[#059669]" strokeWidth={1.8} />
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#059669]">
                {lang === "fr" ? "Forces" : "Strengths"}
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#1F2937]">
              {(strengths || []).map((s, i) => <li key={i}>• {s}</li>)}
              {(!strengths || strengths.length === 0) && <li className="text-xs text-[#8B8B8F]">—</li>}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-6 rounded-lg border border-[#E4E1D8] bg-white p-6">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-[#F2C98A]" strokeWidth={1.8} />
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#F2C98A]">
                {lang === "fr" ? "Axes d'amélioration" : "Improvement axes"}
              </div>
            </div>
            <ul className="space-y-2 text-sm text-[#1F2937]">
              {(gaps || []).map((g, i) => <li key={i}>• {g}</li>)}
              {(!gaps || gaps.length === 0) && <li className="text-xs text-[#8B8B8F]">—</li>}
            </ul>
          </div>
        </div>

        {/* GPS quick access */}
        <Link to="/app/gps" data-testid="dashboard-cta-gps"
              className="block rounded-lg border border-[#E4E1D8] bg-white p-6 hover:border-[#0B2545]/40 altiora-transition">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="absolute -inset-2 rounded-full bg-[#0B2545]/8 blur-md" />
                <svg className="relative h-6 w-6 text-[#0B2545]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path d="M12 22s8-7.58 8-13a8 8 0 10-16 0c0 5.42 8 13 8 13z" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="9" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
                  {lang === "fr" ? "GPS socio-économique" : "Socio-economic GPS"}
                </div>
                <div className="mt-0.5 font-display text-lg text-[#1F2937] tracking-tight">
                  {lang === "fr" ? "Découvrez les opportunités autour de vous" : "Discover opportunities around you"}
                </div>
                <div className="mt-1 text-xs text-[#5B5B62]">
                  {lang === "fr" ? "Emplois, formations, bourses, partenaires — expliqués et priorisés." : "Jobs, training, scholarships, partners — explained and prioritised."}
                </div>
              </div>
            </div>
            <ArrowUpRight className="h-5 w-5 text-[#0B2545]" strokeWidth={1.8} />
          </div>
        </Link>

        {/* CTA footer */}
        <div className="rounded-lg border border-[#E4E1D8] bg-gradient-to-br from-[#FFFFFF] to-[#F8F7F3] p-8 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0B2545]">
              {lang === "fr" ? "Aller plus loin" : "Go further"}
            </div>
            <div className="mt-1 font-display text-2xl text-[#1F2937] tracking-tight">
              {lang === "fr" ? "Découvrez les 30 moteurs ALTIORA" : "Discover the 30 ALTIORA engines"}
            </div>
            <div className="mt-1 text-sm text-[#5B5B62] max-w-xl">
              {lang === "fr"
                ? "Diagnostic, mobilité, opportunités cachées, Life Strategy, White Label… tout est déjà là."
                : "Diagnostic, mobility, hidden opportunities, Life Strategy, White Label… all already here."}
            </div>
          </div>
          <Link to="/app/modules" data-testid="dashboard-cta-modules"
                className="inline-flex items-center gap-2 border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white text-sm font-medium px-5 py-2.5 rounded-md altiora-transition">
            <LayoutGrid className="h-4 w-4" strokeWidth={1.8} />
            {lang === "fr" ? "Voir les moteurs" : "See engines"}
          </Link>
        </div>
      </div>
    </div>
  );
}
